// @ts-nocheck
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Autocomplete,
  Box,
  createFilterOptions,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import isPlainObject from 'lodash/isPlainObject';

import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../const/CommonConst';
import useAttribute from '../../../../hooks/ModelInventory/useAttribute';
import { getErrorHelperText } from '../../../../utils/Utils';
import { DRAFT_INPUT_SX } from './TextFieldComponent';
import { scheduleDraftPersistOnBlur } from '../../../../hooks/useDraftAutoSave.jsx';

const getAssociationSelectOptionLabel = (option) => option?.entity_alias ?? '';
const getAssociationSelectGroupBy = (option) => option?.type;

// Custom filter to search on entity_alias and unique_id
const associationFilterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option) =>
    `${option?.entity_alias || ''} ${option?.unique_id || ''}`,
});

const renderAssociationOption = (props, option) => {
  const { key: _key, ...restProps } = props;
  return (
    <Box component="li" key={option?.entity_id} {...restProps}>
      <Typography variant="body1">
        {getAssociationSelectOptionLabel(option)}&nbsp;
        {option?.unique_id && (
          <Typography variant="caption" component="span">
            ({option?.unique_id})
          </Typography>
        )}
      </Typography>
    </Box>
  );
};

/**
 * Association Select field component for selecting model associations
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {string} props.value - Current selected association id
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - The attribute being edited
 * @param {Object} props.entityDetailsFormikForm - Formik form instance
 * @param {Object} props.formData - Complete form data for API payload
 * @returns {React.ReactElement} The AssociationFieldComponent
 */
const AssociationFieldComponent = ({
  label,
  titleLabel,
  value,
  onChange,
  attribute,
  required = false,
  entityDetailsFormikForm,
  formData = EMPTY_OBJECT,
  entityData = EMPTY_OBJECT,
  loader,
  hasDraftChip = false,
  onDraftBlur,
  onResolvedAssociationChange,
}) => {
  const { getAssociationAttributesData } = useAttribute();
  const [associationOptions, setAssociationOptions] = useState(EMPTY_ARRAY);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const initialFetchContextRef = useRef(null);

  const uniqueFilteredAssociation = useMemo(() => {
    const seen = new Set();
    const deduped = (associationOptions || []).filter((item) => {
      let key = '';

      if (item.entity_id != null) {
        key = `association:${item.entity_id}`;
      } else {
        return null;
      }

      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    return deduped.sort((a, b) =>
      getAssociationSelectOptionLabel(a).localeCompare(
        getAssociationSelectOptionLabel(b),
        undefined,
        { sensitivity: 'base' }
      )
    );
  }, [associationOptions]);

  const selectedAssociation = useMemo(() => {
    return uniqueFilteredAssociation.find((association) => {
      if (isPlainObject(value)) {
        return (
          association?.entity_id === value?.entity_id ||
          association?.unique_id === value?.unique_id
        );
      }

      return (
        association?.entity_id === value || association?.unique_id === value
      );
    });
  }, [uniqueFilteredAssociation, value]);

  useEffect(() => {
    onResolvedAssociationChange?.(selectedAssociation || null);
  }, [selectedAssociation]);
  const fetchAssociationData = useCallback(() => {
    const sourceAttributeId = Number.parseInt(attribute?.id);

    if (
      isLoading ||
      !entityData?.entity_id ||
      !entityData?.entity_type ||
      Number.isNaN(sourceAttributeId)
    ) {
      return;
    }
    setIsLoading(true);
    const payload = {
      source_attribute_id: sourceAttributeId,
      form_data: formData,
      ...(entityData?.draft_entity_key
        ? { draft_entity_key: entityData?.draft_entity_key }
        : { entity_id: entityData?.entity_id }),
      entity_type: entityData?.entity_type,
    };

    getAssociationAttributesData(payload, loader)
      .then((res) => {
        if (res?.data) {
          setAssociationOptions(res.data);
        } else {
          setAssociationOptions(EMPTY_ARRAY);
          // Clear selected value if API fails or returns empty
          if (value) {
            onChange(null);
          }
        }
      })
      .catch(() => {
        setAssociationOptions(EMPTY_ARRAY);
        // Clear selected value on API error
        if (value) {
          onChange(null);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    attribute?.id,
    formData,
    getAssociationAttributesData,
    isLoading,
    value,
    onChange,
    entityData,
  ]);

  const handleDropdownOpen = useCallback(() => {
    setIsDropdownOpen(true);
    fetchAssociationData();
  }, [fetchAssociationData]);

  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false);
    onDraftBlur?.();
  }, [setIsDropdownOpen, onDraftBlur]);

  const handleBlur = useCallback(() => {
    entityDetailsFormikForm?.setFieldTouched?.(attribute?.id, true, true);
  }, [entityDetailsFormikForm, attribute?.id]);

  const renderAssociationSelectTextField = useCallback(
    (params) => (
      <TextField
        {...params}
        label={label}
        title={titleLabel}
        required={required}
        placeholder="Select association"
        error={Boolean(
          getErrorHelperText(
            entityDetailsFormikForm?.errors?.[attribute?.id],
            entityDetailsFormikForm?.touched?.[attribute?.id]
          )
        )}
        helperText={getErrorHelperText(
          entityDetailsFormikForm?.errors?.[attribute?.id],
          entityDetailsFormikForm?.touched?.[attribute?.id]
        )}
        sx={hasDraftChip ? DRAFT_INPUT_SX : undefined}
        slotProps={{
          ...params?.slotProps,
          input: {
            ...params?.slotProps?.input,
            placeholder: 'Select association',
          },

          inputLabel: {
            ...params?.slotProps?.inputLabel,
            shrink: true,
          },
        }}
      />
    ),
    [entityDetailsFormikForm, attribute, label, titleLabel, hasDraftChip]
  );

  const handleAssociationChange = useCallback(
    (_e, newAssociationValue) => {
      if (newAssociationValue) {
        onChange(newAssociationValue);
      } else {
        onChange(null);
        scheduleDraftPersistOnBlur(onDraftBlur);
      }
    },
    [onChange, onDraftBlur]
  );

  useEffect(() => {
    const sourceAttributeId = Number.parseInt(attribute?.id);
    const entityContextKey = [
      sourceAttributeId,
      entityData?.entity_id,
      entityData?.entity_type,
    ].join(':');

    if (
      Number.isNaN(sourceAttributeId) ||
      !entityData?.entity_id ||
      !entityData?.entity_type ||
      initialFetchContextRef.current === entityContextKey
    ) {
      return;
    }

    initialFetchContextRef.current = entityContextKey;
    fetchAssociationData();
  }, [attribute?.id, entityData?.entity_id, entityData?.entity_type]);

  const isOptionEqualToValue = useCallback((option, val) => {
    return option?.entity_id === val?.entity_id;
  }, []);

  return (
    <Autocomplete
      label={label}
      title={titleLabel}
      value={selectedAssociation || value || null}
      options={uniqueFilteredAssociation || []}
      popupIcon={<DropdownArrow />}
      getOptionLabel={getAssociationSelectOptionLabel}
      groupBy={getAssociationSelectGroupBy}
      renderInput={renderAssociationSelectTextField}
      onChange={handleAssociationChange}
      renderOption={renderAssociationOption}
      onOpen={handleDropdownOpen}
      onClose={handleDropdownClose}
      onBlur={handleBlur}
      open={isDropdownOpen}
      loading={isLoading}
      loadingText="Loading associations..."
      filterOptions={associationFilterOptions}
      isOptionEqualToValue={isOptionEqualToValue}
    />
  );
};

AssociationFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.number,
    description: PropTypes.string,
    association_template: PropTypes.shape({
      template_name: PropTypes.string,
      template_type: PropTypes.string,
    }),
  }).isRequired,
  entityDetailsFormikForm: PropTypes.oneOfType([PropTypes.array]).isRequired,
  formData: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  entityData: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  loader: PropTypes.bool,
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
  onResolvedAssociationChange: PropTypes.func,
};

export default AssociationFieldComponent;
